import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { MODULE_CATALOG, ROLE_LABELS, moduleCanDo } from "../lib/modules";
import { ArrowUpRight, AlertCircle, CalendarDays, Eye, EyeOff, Phone, IndianRupee, UserMinus, Undo2, TrendingUp, Lock, Wallet, PlaneTakeoff, ClipboardList, Laptop, Banknote, ListChecks, AlertTriangle, ClipboardCheck, Flame, Ticket, Users, Bell, ChevronsUp, X, FileText } from "lucide-react";
import PunchWidget from "../components/PunchWidget";
import { SalaryAdvanceTile } from "./PayrollTabs";
import { SalaryHikeSubmitDialog } from "./SalaryHike";
import { LeaveBalanceCard, WorkspaceSnapshotRow, ApprovalsSnapshotRow } from "../components/DashboardCards";
import { InterviewDetail } from "./Interview";
import AnnouncementsWidget from "../components/AnnouncementsWidget";
import { OverdueCallRequestsWidget } from "../components/LeadCallRequestsWidgets";
import ProfileQuickActions from "../components/ProfileQuickActions";
import ApprovalLogTile from "../components/ApprovalLogTile";
import Tasks from "./Tasks";
import MyAssets from "./MyAssets";
import TravelBooking from "./TravelBooking";
import TravelAdvancesTab from "./TravelAdvancesTab";
import TravelSettlementsTab from "./TravelSettlementsTab";
import { RecordsPage } from "./ModulePage";
import LeaveManagement from "./LeaveManagement";
import DailyReport from "./DailyReport";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { Button } from "../components/ui/button";

import { fmtDateStr } from "../lib/datetime";

export default function Dashboard() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  // v109.9.56 — Quick-action modals are lifted to Dashboard root so both
  // EmotionalHero (quick-actions grid) AND PunchWidget (Submit Report button)
  // can invoke the same in-place modal system.
  const [activeQA, setActiveQA] = useState(null);
  const openQA = (key) => setActiveQA({ key, n: Date.now() });
  // v109.9.72 — read-only banner for today's daily report (opened via
  // PunchWidget's "View Today's Report" button).
  const [reportBanner, setReportBanner] = useState(null);

  useEffect(() => {
    api.get("/dashboard/summary").then(({ data }) => setSummary(data)).catch(() => {});
  }, []);

  const isMaster = user?.role === "master_admin";
  const accessibleSet = new Set((summary?.accessible_modules || []).map((m) => (typeof m === "string" ? m : m.key)));
  const canAccess = (key) => isMaster || accessibleSet.has(key);

  return (
    <div className="space-y-6 max-w-[1400px]" data-dashboard-root>
      {/* Emotional Hero — time-aware greeting + today's pulse */}
      <EmotionalHero user={user} summary={summary} canAccess={canAccess} openQA={openQA} />

      {/* Punch widget — visible to all users */}
      <PunchWidget
        onSubmitReport={() => openQA("submit-report")}
        onViewReport={(report) => setReportBanner(report)}
      />

      {/* v109.9.72 — In-place read-only banner for today's submitted report */}
      {reportBanner && (
        <TodaysReportBanner report={reportBanner} onClose={() => setReportBanner(null)} />
      )}

      {/* Announcements feed — pinned at top of dashboard so users see latest news first */}
      <AnnouncementsWidget />

      {/* Trainer / admin reminder: pending date-confirmation calls */}
      <PendingDateConfirmationsBanner />

      {/* Overdue call requests — admin escalation only */}
      <OverdueCallRequestsWidget />

      {/* Trainer-only: at-a-glance view of assigned + upcoming + pending requests */}
      <TrainerAssignmentsWidget />

      {/* Trainer-only: projects where this trainer is the assigned Tester */}
      <TesterReviewsWidget />

      {/* Combined Personal Strip: Resign · Salary Hike · My Sales (FY) */}
      <PersonalStrip />

      {/* v109.9.31 — Approval log tile + Request Salary Advance tile, side-by-side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <ApprovalLogTile />
        <SalaryAdvanceTile />
      </div>

      {/* Today's CRM Follow-ups + My Salary (privacy-gated) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <TodayFollowupsWidget />
        </div>
        <MySalaryWidget />
      </div>

      {/* Quick personal stats — My Tasks, Overdue, Pending Review + (master) All Overdue & Open Tickets */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 gap-3 ${isMaster ? "lg:grid-cols-5" : "lg:grid-cols-3"}`}>
        <StatCard label="My Open Tasks" value={summary?.counts?.my_open_tasks ?? "—"} testid="stat-my-tasks" linkTo="/tasks" tone="blue" icon={ListChecks} />
        <StatCard label="Overdue Tasks" value={summary?.counts?.my_overdue_tasks ?? "—"} testid="stat-overdue-tasks" linkTo="/tasks" danger={summary?.counts?.my_overdue_tasks > 0} icon={AlertTriangle} tone="amber" />
        <StatCard label="Pending My Review" value={summary?.counts?.tasks_pending_review ?? "—"} testid="stat-pending-review" linkTo="/tasks" tone="indigo" icon={ClipboardCheck} />
        {isMaster && (
          <>
            <StatCard label="All Tasks Overdue" value={summary?.counts?.all_overdue_tasks ?? "—"} testid="stat-all-overdue" linkTo="/tasks" danger={summary?.counts?.all_overdue_tasks > 0} icon={Flame} tone="orange" />
            <StatCard label="Open Tickets" value={summary?.counts?.it_tickets ?? "—"} testid="stat-tickets" linkTo="/m/it_tickets" tone="emerald" icon={Ticket} />
          </>
        )}
      </div>

      {/* Personal pulse — 4 equally-weighted metric tiles */}
      <LeaveBalanceCard user={user} />

      {/* Workspace snapshot — Travel Advance · Active Tasks · Documentation Corner */}
      <WorkspaceSnapshotRow />

      {/* Approvals snapshot — Pending Disbursement · Pending Call Requests · Upcoming Interviews */}
      <ApprovalsSnapshotRow />

      {/* Banner removed — quick actions live up top now */}

      {/* v109.9.56 — Quick-action modal mount-point (hidden portal container) */}
      <QuickActionMount active={activeQA} onDone={() => setActiveQA(null)} />
    </div>
  );
}

/**
 * Beautiful 3-card strip combining "My Sales (FY)", "Salary Hike", and
 * "Resign from your post". Each card has its own gradient background,
 * iconography, and decorative accents.
 */

// ---------------------------------------------------------------------------
// EmotionalHero — time-aware greeting + rotating quote + today's pulse +
// quick actions. The single block that sets the tone for the whole portal.
// ---------------------------------------------------------------------------
const DAILY_QUOTES = [
  { q: "Small steps, every day, become extraordinary years.", a: "Panchakosh" },
  { q: "Be the calm in someone else's storm today.", a: "Anonymous" },
  { q: "The best way to predict the future is to build it.", a: "Peter Drucker" },
  { q: "Quality means doing it right when no one is looking.", a: "Henry Ford" },
  { q: "Slow is smooth. Smooth is fast.", a: "Navy SEALs" },
  { q: "Work like there is someone working twenty-four hours a day to take it all away.", a: "Mark Cuban" },
  { q: "Done is better than perfect — ship it, learn, ship again.", a: "Sheryl Sandberg" },
  { q: "Make each day your masterpiece.", a: "John Wooden" },
  { q: "Discipline equals freedom.", a: "Jocko Willink" },
  { q: "Energy and persistence conquer all things.", a: "Benjamin Franklin" },
  { q: "The expert in anything was once a beginner.", a: "Helen Hayes" },
  { q: "Stay hungry. Stay foolish.", a: "Steve Jobs" },
  { q: "Action is the foundational key to all success.", a: "Pablo Picasso" },
  { q: "The harder you work for something, the greater you'll feel when you achieve it.", a: "Anonymous" },
];

function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 5) return { greeting: "Burning the midnight oil", emoji: "✨", glow: "from-violet-500/30 via-indigo-500/15 to-transparent", dot: "bg-violet-400" };
  if (hour < 12) return { greeting: "Good morning", emoji: "☀️", glow: "from-amber-400/30 via-orange-300/15 to-transparent", dot: "bg-amber-400" };
  if (hour < 17) return { greeting: "Good afternoon", emoji: "🌤", glow: "from-sky-400/30 via-cyan-300/15 to-transparent", dot: "bg-sky-400" };
  if (hour < 20) return { greeting: "Good evening", emoji: "🌆", glow: "from-rose-400/30 via-orange-400/15 to-transparent", dot: "bg-rose-400" };
  return { greeting: "Good night", emoji: "🌙", glow: "from-indigo-500/30 via-violet-500/15 to-transparent", dot: "bg-indigo-400" };
}

// Smooth count-up hook for stat values.
function useCountUp(target, duration = 800) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (typeof target !== "number") { setValue(target); return; }
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      setValue(Math.round(eased * target));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return value;
}

function LiveClock() {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 30000); // refresh every 30s
    return () => clearInterval(id);
  }, []);
  const time = now.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
  return (
    <span className="mono tabular-nums" data-keep-nowrap>{time}</span>
  );
}

function EmotionalHero({ user, summary, canAccess, openQA }) {
  void canAccess; // reserved for future gating hooks
  const navigate = useNavigate();
  // v109.9.156 — Policy-gated quick actions: if the relevant policy isn't
  // acknowledged, silently route to the module's Policy tab instead of
  // opening the record form (no toast, no banner — per user request).
  const openQuickAction = async (key) => {
    // Leave-policy gate → Apply Leave
    if (key === "apply-leave") {
      try {
        const { data } = await api.get("/leave/policy/my-status");
        if (!data?.acknowledged) {
          navigate("/m/hr_leaves?tab=policy");
          return;
        }
      } catch { /* ignore — fall through to normal open */ }
    }
    // Travel-policy gate → Book Travel, Apply Advance, Advance Settlement
    if (["travel-booking", "travel-advance", "advance-settlement"].includes(key)) {
      try {
        const { data } = await api.get("/travel-policy/acknowledgment");
        if (!data?.acknowledged) {
          navigate("/m/travel?tab=policy");
          return;
        }
      } catch { /* ignore */ }
    }
    (openQA || (() => {}))(key);
  };
  const greeting = useMemo(getGreeting, []);
  const quote = useMemo(() => {
    const dayKey = Math.floor(Date.now() / (1000 * 60 * 60 * 24));
    return DAILY_QUOTES[dayKey % DAILY_QUOTES.length];
  }, []);
  const dateStr = useMemo(() => {
    const d = new Date();
    return d.toLocaleDateString("en-IN", { weekday: "long", day: "numeric", month: "long" });
  }, []);
  const firstName = user?.name?.split(" ")[0] || "there";

  return (
    <div className="relative pk-depth pk-depth-indigo rounded-2xl overflow-hidden">
      {/* Ambient time-aware aurora glow (top-right corner). */}
      <div
        className={`absolute -top-32 -right-20 w-[420px] h-[420px] rounded-full blur-3xl bg-gradient-to-br ${greeting.glow} pointer-events-none`}
        data-keep-gradient
        data-keep-color
      />
      {/* Subtle indigo aurora on bottom-left. */}
      <div
        className="absolute -bottom-32 -left-20 w-[360px] h-[360px] rounded-full blur-3xl bg-gradient-to-tr from-indigo-500/20 via-blue-500/10 to-transparent pointer-events-none"
        data-keep-gradient
        data-keep-color
      />
      {/* Dotted grid pattern — adds texture without noise. */}
      <div
        className="absolute inset-0 opacity-[0.35] dark:opacity-[0.18] pointer-events-none"
        style={{
          backgroundImage: "radial-gradient(circle, hsl(var(--muted-foreground) / 0.18) 1px, transparent 1px)",
          backgroundSize: "22px 22px",
        }}
      />

      <div className="relative px-5 sm:px-8 py-7 sm:py-8">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          {/* Left: greeting + meta + quote */}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2.5 mb-3 flex-wrap">
              <span className={`relative flex w-2 h-2`}>
                <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${greeting.dot} opacity-60`} data-keep-color />
                <span className={`relative inline-flex rounded-full h-2 w-2 ${greeting.dot}`} data-keep-color />
              </span>
              <span className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-[hsl(var(--muted-foreground))]">
                {dateStr}
              </span>
              <span className="text-[hsl(var(--muted-foreground)/0.4)]">·</span>
              <span className="text-[10.5px] uppercase tracking-[0.22em] font-semibold text-[hsl(var(--muted-foreground))]">
                <LiveClock />
              </span>
              <span className="text-[hsl(var(--muted-foreground)/0.4)] hidden sm:inline">·</span>
              <span className="hidden sm:inline text-[10.5px] uppercase tracking-[0.22em] font-semibold text-[hsl(var(--muted-foreground))]">
                {ROLE_LABELS[user?.role] || user?.role}
              </span>
              <span className="ml-auto">
                <ProfileQuickActions />
              </span>
            </div>
            <h1 className="text-[28px] sm:text-[38px] lg:text-[44px] font-black tracking-tight leading-[1.05] text-[hsl(var(--foreground))]">
              {greeting.greeting},{" "}
              <span className="text-[hsl(var(--primary))]">{firstName}</span>
              <span className="ml-2.5 inline-block align-middle" aria-hidden="true">{greeting.emoji}</span>
            </h1>
            <p className="mt-2 text-[13.5px] text-[hsl(var(--muted-foreground))] max-w-lg leading-relaxed">
              Here&apos;s a calm, focused look at your day. Take it one block at a time.
            </p>
            <div className="mt-4 inline-flex items-start gap-2.5 px-4 py-2.5 rounded-xl bg-[hsl(var(--muted))] border border-[hsl(var(--border))] max-w-xl">
              <span className="text-[hsl(var(--primary)/0.6)] text-[22px] leading-none mt-0.5 font-serif">&ldquo;</span>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] text-[hsl(var(--foreground))] leading-snug italic">{quote.q}</div>
                <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1 uppercase tracking-[0.15em] font-semibold">&mdash; {quote.a}</div>
              </div>
            </div>
          </div>

          {/* Right: today's pulse stats */}
          <DayPulse summary={summary} />
        </div>

        {/* Quick Actions Row */}
        <div className="mt-7 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5" data-testid="dash-quick-actions">
          <QuickActionButton onClick={() => openQuickAction("travel-booking")}   icon={PlaneTakeoff}  label="Book Travel"        sublabel="Flights · Trains"  tone="blue"    testid="quick-action-travel" />
          <QuickActionButton onClick={() => openQuickAction("travel-advance")}   icon={Banknote}      label="Apply Advance"      sublabel="Travel · Cash"     tone="indigo"  testid="quick-action-advance" />
          <QuickActionButton onClick={() => openQuickAction("request-asset")}    icon={Laptop}        label="Request Asset"      sublabel="Laptop · Phone"    tone="sky"     testid="quick-action-asset" />
          <QuickActionButton onClick={() => openQuickAction("apply-leave")}      icon={CalendarDays}  label="Apply Leave"        sublabel="Casual · Sick"     tone="emerald" testid="quick-action-leave" />
          <QuickActionButton onClick={() => openQuickAction("advance-settlement")} icon={Wallet}      label="Advance Settlement" sublabel="Settle advances"   tone="amber"   testid="quick-action-advance-settlement" />
          <QuickActionButton onClick={() => openQuickAction("new-task")}         icon={ClipboardList} label="New Task"           sublabel="Plan your work"    tone="violet"  testid="quick-action-task" />
        </div>
      </div>
    </div>
  );
}

/** Renders the currently-selected quick-action's destination component in an
 *  aria-hidden offscreen container. The child auto-opens its own new-record
 *  dialog via `actionPulse`, which portals to <body> and is fully visible.  */
function QuickActionMount({ active, onDone }) {
  if (!active) return null;
  const pulse = { key: "new", n: active.n };
  const HIDE = {
    position: "fixed",
    left: 0,
    top: 0,
    width: 0,
    height: 0,
    overflow: "hidden",
    opacity: 0,
    pointerEvents: "none",
    zIndex: -1,
  };
  return (
    // v109.9.154 — Do NOT wire onClick={onDone} here. Radix Dialog content is
    // portalled to <body> but still bubbles React synthetic events up through
    // this parent — every click inside the child dialog was firing onDone and
    // instantly closing the "Submit Daily Report" dialog. onDone is now
    // triggered only by the child's explicit save/close flow (or the next
    // quick-action click).
    <div style={HIDE} aria-hidden="true" data-testid="quick-action-mount">
      {active.key === "travel-booking" && (
        <TravelBooking embedded actionPulse={pulse} />
      )}
      {active.key === "travel-advance" && (
        <TravelAdvancesTab actionPulse={pulse} />
      )}
      {active.key === "advance-settlement" && (
        <TravelSettlementsTab actionPulse={pulse} />
      )}
      {active.key === "request-asset" && (
        <MyAssets actionPulse={pulse} />
      )}
      {active.key === "apply-leave" && (
        <LeaveManagement
          key={`qa-leave-${active.n}`}
          embedded
          actionPulse={pulse}
        />
      )}
      {active.key === "submit-report" && (
        <DailyReport
          key={`qa-report-${active.n}`}
          embedded
          actionPulse={pulse}
        />
      )}
      {active.key === "new-task" && (
        <Tasks actionPulse={pulse} />
      )}
    </div>
  );
}

// v109.9.72 — Read-only banner that renders today's submitted daily report
// inline on the dashboard (no navigation away). Small × in the top-right
// closes it. Pulled straight from `/my-daily-report-status.report`.
function TodaysReportBanner({ report, onClose }) {
  const submittedAt = report?.created_at
    ? new Date(report.created_at).toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short",
      })
    : "—";
  return (
    <div
      className="pk-depth pk-depth-emerald relative overflow-hidden rounded-xl border border-emerald-200 dark:border-emerald-500/30 bg-white dark:bg-slate-900 p-4"
      data-testid="todays-report-banner"
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-2 right-2 w-7 h-7 grid place-items-center rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-white transition-all"
        data-testid="todays-report-banner-close"
        title="Close"
      >
        <X size={14} />
      </button>
      <div className="flex items-center gap-2 mb-2 pr-8">
        <FileText size={16} className="text-emerald-600" />
        <div>
          <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-emerald-700 dark:text-emerald-300">
            Today&apos;s Daily Report · read-only
          </div>
          <div className="text-[11px] text-slate-500 dark:text-slate-400 mono">
            Submitted {submittedAt}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[12.5px]">
        <ReportField label="Tasks Completed" value={report?.tasks_completed} testid="todays-report-tasks" />
        <ReportField label="Challenges" value={report?.challenges} testid="todays-report-challenges" />
        <ReportField label="Next Day Plan" value={report?.next_day_plan} testid="todays-report-plan" />
      </div>
    </div>
  );
}

function ReportField({ label, value, testid }) {
  const text = (value == null || String(value).trim() === "") ? "—" : String(value);
  return (
    <div className="rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50/60 dark:bg-slate-800/40 p-3">
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 dark:text-slate-400 mb-1">{label}</div>
      <div className="text-slate-800 dark:text-slate-100 whitespace-pre-wrap break-words" data-testid={testid}>{text}</div>
    </div>
  );
}



function DayPulse({ summary }) {
  const pulses = useMemo(() => {
    const ws = summary?.workspace_snapshot || {};
    const ap = summary?.approvals_snapshot || {};
    const items = [];
    if (typeof ws.my_open_tasks === "number") items.push({ label: "Open tasks", value: ws.my_open_tasks, tone: "indigo", icon: ClipboardList });
    if (typeof ws.my_pending_assets === "number") items.push({ label: "My assets", value: ws.my_pending_assets, tone: "sky", icon: Laptop });
    if (typeof ap.pending_approvals === "number") items.push({ label: "To approve", value: ap.pending_approvals, tone: "amber", icon: AlertTriangle });
    if (typeof summary?.unread_announcements === "number") items.push({ label: "Unread news", value: summary.unread_announcements, tone: "rose", icon: Bell });
    return items.slice(0, 4);
  }, [summary]);
  if (pulses.length === 0) return null;
  return (
    <div className="grid grid-cols-2 gap-2.5 shrink-0 no-mobile-stack" data-testid="dash-day-pulse">
      {pulses.map((p) => <PulseTile key={p.label} {...p} />)}
    </div>
  );
}

function PulseTile({ label, value, icon: Icon }) {
  const animated = useCountUp(typeof value === "number" ? value : 0);
  return (
    <div className="group pk-depth pk-depth-indigo relative rounded-xl px-3.5 py-2.5 min-w-[126px] overflow-hidden">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[9.5px] uppercase tracking-[0.16em] font-semibold text-[hsl(var(--muted-foreground))] truncate">{label}</div>
        {Icon && <Icon size={12} className="text-[hsl(var(--muted-foreground))] opacity-60" />}
      </div>
      <div className="text-[26px] font-black tabular-nums tracking-tight text-[hsl(var(--foreground))] leading-none mt-1.5 mono">
        {animated}
      </div>
      {/* subtle indigo accent line on hover */}
      <div className="absolute left-0 right-0 bottom-0 h-[2px] bg-[hsl(var(--primary))] opacity-0 group-hover:opacity-100 transition-opacity" data-keep-color />
    </div>
  );
}


function PersonalStrip() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="personal-strip">
      <MySalesSummaryCompact />
      <SalaryHikeWidget compact />
      <ResignationDashboardSection compact />
    </div>
  );
}

function QuickActionButton({ to, onClick, icon: Icon, label, sublabel, tone = "blue", disabled, testid }) {
  const toneMap = {
    blue:    { iconBg: "bg-blue-50",    iconFg: "text-blue-700",    bar: "bg-blue-500" },
    indigo:  { iconBg: "bg-indigo-50",  iconFg: "text-indigo-700",  bar: "bg-indigo-500" },
    sky:     { iconBg: "bg-sky-50",     iconFg: "text-sky-700",     bar: "bg-sky-500" },
    emerald: { iconBg: "bg-emerald-50", iconFg: "text-emerald-700", bar: "bg-emerald-500" },
    amber:   { iconBg: "bg-amber-50",   iconFg: "text-amber-700",   bar: "bg-amber-500" },
    violet:  { iconBg: "bg-violet-50",  iconFg: "text-violet-700",  bar: "bg-violet-500" },
  };
  const t = toneMap[tone] || toneMap.blue;
  if (disabled) {
    return (
      <div
        className="group relative flex items-center gap-3 rounded-lg px-3.5 py-3 bg-white border border-slate-200 cursor-not-allowed opacity-60"
        data-testid={testid}
        title="You don't have access to this module"
      >
        <div className="w-9 h-9 rounded-md bg-slate-100 flex items-center justify-center flex-shrink-0">
          <Icon size={17} className="text-slate-400" strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[12.5px] font-semibold text-slate-500 truncate">{label}</div>
          <div className="text-[10.5px] text-slate-400 truncate">No access</div>
        </div>
      </div>
    );
  }
  const commonInner = (
    <>
      <span className={`absolute left-0 top-0 bottom-0 w-[3px] ${t.bar} opacity-80`} data-keep-color />
      <div className={`w-9 h-9 rounded-md ${t.iconBg} border border-slate-200 flex items-center justify-center flex-shrink-0`} data-keep-color>
        <Icon size={17} className={t.iconFg} strokeWidth={2} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[12.5px] font-semibold text-slate-900 truncate flex items-center gap-1">
          {label}
          <ArrowUpRight size={11} className="text-slate-400 group-hover:text-slate-600 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition" />
        </div>
        <div className="text-[10.5px] text-slate-500 truncate">{sublabel}</div>
      </div>
    </>
  );
  // Button variant (opens a modal on the current page — no navigation).
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className="group relative flex items-center gap-3 rounded-lg px-3.5 py-3 bg-white border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all overflow-hidden text-left w-full"
        data-testid={testid}
      >
        {commonInner}
      </button>
    );
  }
  return (
    <Link
      to={to}
      className="group relative flex items-center gap-3 rounded-lg px-3.5 py-3 bg-white border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all overflow-hidden"
      data-testid={testid}
    >
      {commonInner}
    </Link>
  );
}

function StatCard({ label, value, testid, linkTo, danger, icon: Icon = ListChecks, tone = "blue", sublabel }) {
  const toneMap = {
    blue:    { border: "border-blue-200",    bg: "from-blue-50 via-white to-indigo-50/60",    blob: "bg-blue-200/40",    iconColor: "text-blue-600",    label: "text-blue-700" },
    rose:    { border: "border-rose-200",    bg: "from-rose-50 via-white to-pink-50/60",      blob: "bg-rose-200/40",    iconColor: "text-rose-600",    label: "text-rose-700" },
    amber:   { border: "border-amber-200",   bg: "from-amber-50 via-white to-orange-50/60",   blob: "bg-amber-200/40",   iconColor: "text-amber-600",   label: "text-amber-700" },
    orange:  { border: "border-orange-200",  bg: "from-orange-50 via-white to-red-50/60",     blob: "bg-orange-200/40",  iconColor: "text-orange-600",  label: "text-orange-700" },
    indigo:  { border: "border-indigo-200",  bg: "from-indigo-50 via-white to-violet-50/60",  blob: "bg-indigo-200/40",  iconColor: "text-indigo-600",  label: "text-indigo-700" },
    emerald: { border: "border-emerald-200", bg: "from-emerald-50 via-white to-teal-50/60",   blob: "bg-emerald-200/40", iconColor: "text-emerald-600", label: "text-emerald-700" },
  };
  const t = toneMap[danger ? "rose" : tone] || toneMap.blue;
  const Wrapper = linkTo ? Link : "div";
  const props = linkTo ? { to: linkTo } : {};
  return (
    <Wrapper
      {...props}
      className={`relative overflow-hidden rounded-xl pk-depth pk-depth-${danger ? "rose" : tone} p-4 block group h-full`}
      data-testid={testid}
    >
      <div className={`absolute -top-8 -right-8 w-20 h-20 ${t.blob} rounded-full blur-2xl group-hover:opacity-80 transition-opacity`} />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className={`text-[10px] uppercase tracking-[0.16em] font-bold ${t.label}`}>{label}</div>
          <div className={`heading text-3xl font-extrabold mono mt-1.5 leading-none ${danger ? "text-rose-700" : "text-[hsl(var(--foreground))]"}`}>{value}</div>
          {sublabel && <div className="text-[10.5px] text-[hsl(var(--muted-foreground))] mt-1.5">{sublabel}</div>}
        </div>
        <Icon size={26} strokeWidth={2} className={`${t.iconColor} shrink-0 group-hover:scale-110 transition-transform`} />
      </div>
    </Wrapper>
  );
}

function MySalesSummary() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/my-sales").then(({ data }) => setData(data)).catch(() => {});
  }, []);
  if (!data) return null;
  if (!data.fy_total_approved && !data.fy_total_pending) return null;
  const fmt = (n) => "₹ " + (Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  return (
    <Link to="/my-sales" className="pk-card p-5 block hover:-translate-y-px hover:shadow-sm transition-all border-emerald-200 bg-gradient-to-r from-emerald-50/40 to-amber-50/30" data-testid="dash-my-sales-summary">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-wider font-semibold text-emerald-700">My Sales — {data.fy_label}</div>
          <div className="mt-1 flex items-baseline gap-3 flex-wrap">
            <span className="heading text-3xl font-extrabold mono text-slate-900">{fmt(data.fy_total_approved)}</span>
            <span className="text-[11px] text-slate-500">approved</span>
            {data.fy_total_pending > 0 && <span className="text-[11px] text-amber-700 font-semibold">+ {fmt(data.fy_total_pending)} pending</span>}
          </div>
          <div className="text-[11px] text-slate-600 mt-1.5">
            This month: <span className="font-semibold mono">{fmt(data.this_month_approved)}</span>
            {data.this_month_pending > 0 && <span className="text-amber-700"> · +{fmt(data.this_month_pending)} pending</span>}
          </div>
        </div>
        <ArrowUpRight size={20} className="text-emerald-600" />
      </div>
    </Link>
  );
}

function MySalesSummaryCompact() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/my-sales").then(({ data }) => setData(data)).catch(() => {});
  }, []);
  const fmt = (n) => "₹" + (Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  const hasData = data && (data.fy_total_approved || data.fy_total_pending);
  return (
    <Link
      to="/my-sales"
      className="pk-depth pk-depth-emerald relative overflow-hidden rounded-xl p-5 block group"
      data-testid="strip-my-sales"
    >
      {/* Decorative blob */}
      <div className="absolute -top-8 -right-8 w-24 h-24 bg-emerald-200/40 rounded-full blur-2xl group-hover:bg-emerald-300/40 transition-colors" />
      <div className="relative flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp size={22} strokeWidth={2} className="text-emerald-600 shrink-0 group-hover:scale-110 transition-transform" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-emerald-700">My Sales</div>
              <div className="text-[10.5px] text-emerald-600/80 font-semibold">{data?.fy_label || "Current FY"}</div>
            </div>
          </div>

          {hasData ? (
            <>
              <div className="heading text-3xl font-extrabold mono text-slate-900 mt-2 leading-none tracking-tight">
                {fmt(data.fy_total_approved)}
              </div>
              <div className="text-[11px] text-slate-600 mt-1 font-medium">
                <span className="inline-flex items-center gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Approved
                </span>
                {data.fy_total_pending > 0 && (
                  <span className="ml-3 inline-flex items-center gap-1 text-amber-700">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                    +{fmt(data.fy_total_pending)} pending
                  </span>
                )}
              </div>
              {data.this_month_approved > 0 && (
                <div className="text-[10.5px] text-slate-500 mt-1.5 mono">
                  This month · <span className="font-bold text-slate-700">{fmt(data.this_month_approved)}</span>
                </div>
              )}
            </>
          ) : (
            <div className="text-[12px] text-slate-500 mt-3 italic">No sales recorded yet for this FY.</div>
          )}
        </div>
        <ArrowUpRight size={18} className="text-emerald-600 flex-shrink-0 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
      </div>
    </Link>
  );
}


function TodayFollowupsWidget() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/dashboard/my-followups-today")
      .then(({ data }) => setItems(data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="pk-depth pk-depth-sky relative overflow-hidden rounded-xl p-5 h-full group" data-testid="dash-followups-today">
      <div className="absolute -top-10 -right-10 w-28 h-28 bg-sky-200/40 rounded-full blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div className="flex items-center gap-2">
            <Phone size={22} strokeWidth={2} className="text-sky-600 shrink-0 group-hover:scale-110 transition-transform" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-sky-700">Today&apos;s Follow-ups</div>
              <div className="text-[10.5px] text-sky-600/80 font-semibold">{items.length} scheduled</div>
            </div>
          </div>
          <Link to="/m/sales_crm_report" className="inline-flex items-center gap-0.5 px-3 py-1.5 rounded-full bg-white/80 border border-sky-200 text-[10.5px] font-bold text-sky-700 hover:bg-sky-50 transition-all" data-testid="dash-followups-link">
            Open CRM <ArrowUpRight size={11} />
          </Link>
        </div>

        {loading ? (
          <div className="py-6 text-center text-slate-400 text-[12px]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="py-8 text-center text-slate-400 text-[12px]" data-testid="dash-followups-empty">
            No follow-ups scheduled for today. 🎉
          </div>
        ) : (
          <div className="space-y-1.5" data-testid="dash-followups-list">
            {items.map((it) => (
              <Link
                key={it.id}
                to={`/m/sales_crm_report?client=${it.client_id}`}
                className="flex items-center gap-3 bg-white/70 backdrop-blur-sm border border-sky-100 rounded-lg px-3 py-2 hover:border-sky-300 hover:bg-white transition-all"
                data-testid={`dash-followup-${it.id}`}
              >
                <span className="inline-block h-2 w-2 rounded-full bg-sky-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold text-slate-800 truncate">{it.client_name || "—"}</div>
                  <div className="text-[10.5px] text-slate-500 mt-0.5 truncate">
                    {it.u_dise && <span className="mono">{it.u_dise} · </span>}
                    {it.remark || "No remark"}
                  </div>
                </div>
                {it.lead_status && (
                  <span className="text-[9.5px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ring-1 ring-slate-200 bg-slate-50 text-slate-700 flex-shrink-0">
                    {it.lead_status}
                  </span>
                )}
                <ArrowUpRight size={14} className="text-sky-400 flex-shrink-0" />
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function MySalaryWidget() {
  const [data, setData] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/dashboard/my-salary")
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  const fmt = (n) => "₹ " + (Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });
  const masked = "₹ • • • • •";
  const monthLabel = data?.month
    ? new Date(data.month + "-01").toLocaleString("en-IN", { month: "short", year: "numeric" })
    : "—";

  return (
    <div className="pk-depth pk-depth-emerald relative overflow-hidden rounded-xl p-5 h-full flex flex-col group" data-testid="dash-my-salary">
      <div className="absolute -top-10 -right-10 w-28 h-28 bg-emerald-200/40 rounded-full blur-3xl pointer-events-none" />
      <div className="relative flex items-center justify-between gap-2 mb-3">
        <div className="flex items-center gap-2">
          <IndianRupee size={22} strokeWidth={2} className="text-emerald-600 shrink-0 group-hover:scale-110 transition-transform" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-emerald-700">My Salary</div>
            <div className="text-[10.5px] text-emerald-600/80 font-semibold">{revealed ? "Visible" : "Confidential"}</div>
          </div>
        </div>
        {revealed ? (
          <button
            onClick={() => setRevealed(false)}
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/80 border border-slate-200 text-[10.5px] text-slate-600 hover:bg-white hover:text-slate-800 font-bold transition-all"
            data-testid="dash-salary-hide"
            title="Hide"
          >
            <EyeOff size={11} /> Hide
          </button>
        ) : (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <button
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-emerald-100 border border-emerald-200 text-[10.5px] text-emerald-700 hover:bg-emerald-200 font-bold transition-all"
                data-testid="dash-salary-reveal"
                disabled={loading || !data}
                title="View salary"
              >
                <Eye size={11} /> View
              </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle className="flex items-center gap-2">
                  <AlertCircle size={16} className="text-amber-600" />
                  Confidential Information
                </AlertDialogTitle>
                <AlertDialogDescription className="space-y-2 pt-2">
                  <span className="block text-[13px] text-slate-700 font-semibold">
                    This is confidential. Don&apos;t share with anyone.
                  </span>
                  <span className="block text-[12px] text-slate-600">
                    Are you sure you want to view your salary details?
                  </span>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="dash-salary-confirm-no">No</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() => setRevealed(true)}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="dash-salary-confirm-yes"
                >
                  Yes, View
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>

      <div className="relative space-y-3 flex-1 mt-1">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">Monthly Salary (CTC)</div>
          <div className="heading text-2xl font-extrabold mono text-slate-900 mt-0.5" data-testid="dash-salary-monthly">
            {loading ? "…" : revealed ? fmt(data?.monthly_salary) : masked}
          </div>
        </div>
        <div className="border-t border-emerald-100/70 pt-3">
          <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold flex items-center gap-1.5">
            This Month Calculated · <span className="mono text-slate-400">{monthLabel}</span>
          </div>
          <div className="flex items-baseline gap-2 mt-0.5 flex-wrap">
            <div className="heading text-2xl font-extrabold mono text-emerald-700" data-testid="dash-salary-month-amount">
              {loading ? "…" : revealed ? fmt(data?.this_month_amount) : masked}
            </div>
            {revealed && data?.this_month_status && (
              <span
                className={`text-[9.5px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ring-1 ${
                  data.this_month_status === "Disbursed"
                    ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                    : data.this_month_status === "Calculated"
                    ? "bg-sky-50 text-sky-700 ring-sky-200"
                    : data.this_month_status === "On Hold"
                    ? "bg-amber-50 text-amber-700 ring-amber-200"
                    : "bg-slate-50 text-slate-600 ring-slate-200"
                }`}
                data-testid="dash-salary-status"
              >
                {data.this_month_status}
              </span>
            )}
          </div>
          {revealed && data?.this_month_status !== "Disbursed" && (
            <div className="text-[10px] text-slate-400 mt-1">Awaiting disbursement by Payroll Admin.</div>
          )}
        </div>
      </div>
    </div>
  );
}


function HolidaysAndReportWidget() { return <EventsCalendarPanel />; }


function ResignationDashboardSection({ compact = false }) {
  const [resignation, setResignation] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/my-resignation")
      .then(({ data }) => setResignation(data || null))
      .catch(() => setResignation(null))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const withdraw = async () => {
    if (!resignation?.id) return;
    try {
      await api.post(`/resignations/${resignation.id}/withdraw`);
      toast.success("Resignation withdrawn");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  if (loading) return null;

  // ---------- COMPACT (PersonalStrip section) ----------
  if (compact) {
    const status = resignation?.status;
    const tone = status === "approved"
      ? { border: "border-rose-200", bg: "from-rose-50 via-white to-amber-50/60", blob: "bg-rose-200/40", iconBg: "from-rose-500 to-amber-600", iconRing: "ring-rose-300/40", label: "text-rose-700", sub: "text-rose-600/80", shadow: "hover:shadow-rose-100", title: "Offboarding" }
      : status === "pending"
      ? { border: "border-amber-200", bg: "from-amber-50 via-white to-orange-50/60", blob: "bg-amber-200/40", iconBg: "from-amber-500 to-orange-600", iconRing: "ring-amber-300/40", label: "text-amber-700", sub: "text-amber-600/80", shadow: "hover:shadow-amber-100", title: "Resignation Pending" }
      : { border: "border-slate-200", bg: "from-slate-50 via-white to-rose-50/30", blob: "bg-rose-100/40", iconBg: "from-slate-600 to-slate-700", iconRing: "ring-slate-300/40", label: "text-rose-700", sub: "text-slate-600", shadow: "hover:shadow-rose-100", title: "Resignation" };

    return (
      <div
        className={`pk-depth pk-depth-${status === "approved" ? "rose" : status === "pending" ? "amber" : "rose"} relative overflow-hidden rounded-xl p-5 group`}
        data-testid="strip-resign"
      >
        <div className={`absolute -top-8 -right-8 w-24 h-24 ${tone.blob} rounded-full blur-2xl group-hover:opacity-80 transition-opacity`} />
        <div className="relative">
          <div className="flex items-center gap-2 mb-1">
            <UserMinus size={22} strokeWidth={2} className={`${tone.label} shrink-0 group-hover:scale-110 transition-transform`} />
            <div>
              <div className={`text-[10px] uppercase tracking-[0.16em] font-bold ${tone.label}`}>{tone.title}</div>
              <div className={`text-[10.5px] font-semibold ${tone.sub}`}>
                {status === "approved" ? "In progress" : status === "pending" ? "Awaiting approval" : "Need to resign?"}
              </div>
            </div>
          </div>

          {status === "approved" && (
            <>
              <div className="mt-2 grid grid-cols-2 gap-2">
                <div className="rounded-md bg-white/70 backdrop-blur-sm border border-rose-100 px-2.5 py-1.5">
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">Last Day</div>
                  <div className="mono font-extrabold text-[13px] text-rose-700 truncate">{fmtDateStr(resignation.approved_last_working_day)}</div>
                </div>
                <div className="rounded-md bg-white/70 backdrop-blur-sm border border-amber-100 px-2.5 py-1.5">
                  <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">Days Left</div>
                  <div className="mono font-extrabold text-[13px] text-amber-700">{resignation.days_remaining}</div>
                </div>
              </div>
              <Link
                to="/resignation"
                className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-rose-200 text-[10.5px] font-bold text-rose-700 hover:bg-rose-50 transition-all"
                data-testid="strip-resign-link"
              >
                Open offboarding <ArrowUpRight size={11} />
              </Link>
            </>
          )}

          {status === "pending" && (
            <>
              <div className="mt-2 rounded-md bg-white/70 backdrop-blur-sm border border-amber-100 px-2.5 py-1.5">
                <div className="text-[9px] uppercase tracking-wider text-slate-500 font-bold">Proposed LWD</div>
                <div className="mono font-extrabold text-[13px] text-amber-700">{fmtDateStr(resignation.proposed_last_working_day)}</div>
              </div>
              <div className="mt-3 flex items-center gap-1.5">
                <Link
                  to="/resignation"
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-amber-200 text-[10.5px] font-bold text-amber-800 hover:bg-amber-50 transition-all"
                >
                  Details <ArrowUpRight size={11} />
                </Link>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-rose-50 border border-rose-200 text-[10.5px] font-bold text-rose-700 hover:bg-rose-100 transition-all"
                      data-testid="strip-resign-withdraw"
                    >
                      <Undo2 size={11} /> Withdraw
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Withdraw your resignation?</AlertDialogTitle>
                      <AlertDialogDescription>Your resignation request will be removed. You can re-submit later.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Keep It</AlertDialogCancel>
                      <AlertDialogAction onClick={withdraw} className="bg-amber-600 hover:bg-amber-700">Yes, Withdraw</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </>
          )}

          {!status && (
            <>
              <div className="mt-2 text-[12px] text-slate-700 leading-snug">
                Begin the offboarding process whenever you decide to move on.
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    className="mt-3 inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-gradient-to-br from-rose-600 to-rose-700 text-[10.5px] font-bold text-white shadow-sm hover:shadow-md hover:-translate-y-px transition-all"
                    data-testid="strip-resign-btn"
                  >
                    Resign from Your Post <ArrowUpRight size={11} />
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2"><AlertCircle className="text-rose-600" size={18} /> Begin Resignation?</AlertDialogTitle>
                    <AlertDialogDescription className="space-y-2 pt-1">
                      <span className="block">Clicking <strong>Continue</strong> opens the resignation form.</span>
                      <span className="block font-semibold text-rose-700">After HR / Master Admin approves your resignation, this action becomes <u>irreversible</u>.</span>
                      <span className="block">You will be able to withdraw your request only until approval.</span>
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction asChild className="bg-rose-600 hover:bg-rose-700">
                      <Link to="/resignation">Continue to Resignation Form</Link>
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
        </div>
      </div>
    );
  }

  // Approved resignation → highlight banner with last working day + days remaining
  if (resignation?.status === "approved") {
    return (
      <div className="pk-card p-5 bg-gradient-to-r from-rose-50 to-amber-50 border-rose-200" data-testid="resignation-approved-banner">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] text-rose-700 font-bold flex items-center gap-1.5">
              <UserMinus size={13} /> Resignation Approved · Offboarding in progress
            </div>
            <div className="flex items-baseline gap-3 flex-wrap mt-1.5">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Last Working Day</div>
                <div className="mono font-extrabold text-2xl text-rose-700">{fmtDateStr(resignation.approved_last_working_day)}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Days Remaining</div>
                <div className="mono font-extrabold text-2xl text-amber-700">{resignation.days_remaining}</div>
              </div>
            </div>
          </div>
          <Link to="/resignation" className="text-[12px] font-semibold text-rose-700 hover:underline" data-testid="resignation-banner-link">
            View &amp; complete exit interview →
          </Link>
        </div>
      </div>
    );
  }

  // Pending resignation → show withdraw banner
  if (resignation?.status === "pending") {
    return (
      <div className="pk-card p-4 bg-amber-50 border-amber-200 flex items-center justify-between gap-3 flex-wrap" data-testid="resignation-pending-banner">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-amber-800 font-bold">Resignation Submitted · Pending Approval</div>
          <div className="text-[12.5px] text-amber-700 mt-0.5">Proposed last working day · <span className="mono font-semibold">{fmtDateStr(resignation.proposed_last_working_day)}</span> · You can still withdraw.</div>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/resignation" className="text-[12px] font-semibold text-amber-800 hover:underline" data-testid="resignation-pending-link">View details →</Link>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-100 h-8" data-testid="dash-resign-withdraw">
                <Undo2 size={12} className="mr-1" /> Withdraw
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Withdraw your resignation?</AlertDialogTitle>
                <AlertDialogDescription>Your resignation request will be removed. You can re-submit later.</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Keep It</AlertDialogCancel>
                <AlertDialogAction onClick={withdraw} className="bg-amber-600 hover:bg-amber-700" data-testid="dash-resign-withdraw-confirm">Yes, Withdraw</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    );
  }

  // No active resignation → show the trigger button (subtle, end-of-row in punch area).
  return (
    <div className="flex items-center justify-end" data-testid="resignation-trigger-row">
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="outline" size="sm" className="text-rose-700 border-rose-200 hover:bg-rose-50 h-9 text-[12px]" data-testid="dash-resign-btn">
            <UserMinus size={13} className="mr-1.5" /> Resign from Your Post
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><AlertCircle className="text-rose-600" size={18} /> Begin Resignation?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-2 pt-1">
              <span className="block">Clicking <strong>Continue</strong> opens the resignation form.</span>
              <span className="block font-semibold text-rose-700">After HR / Master Admin approves your resignation, this action becomes <u>irreversible</u>.</span>
              <span className="block">You will be able to withdraw your request only until approval.</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="dash-resign-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction asChild className="bg-rose-600 hover:bg-rose-700">
              <Link to="/resignation" data-testid="dash-resign-continue">Continue to Resignation Form</Link>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
// Re-exported for use on /m/my_attendance (and elsewhere) — keeps a single source.
export function EventsCalendarPanel({ onSubmitReport }) {
  const [events, setEvents] = useState([]);
  const [report, setReport] = useState(null);
  const [viewMonth, setViewMonth] = useState(() => new Date());
  const [activeTab, setActiveTab] = useState("all");

  const monthKey = (d) => {
    if (!d) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    return `${y}-${m}`;
  };

  useEffect(() => {
    api.get(`/my-calendar?month=${monthKey(viewMonth)}`)
      .then(({ data }) => setEvents(data?.events || []))
      .catch(() => setEvents([]));
  }, [viewMonth]);

  useEffect(() => {
    api.get("/my-daily-report-status").then(({ data }) => setReport(data)).catch(() => {});
  }, []);

  const counts = useMemo(() => {
    const c = { holiday: 0, leave: 0, absent: 0, present: 0, report_missing: 0, weekend: 0 };
    (events || []).forEach((e) => { if (c[e.type] != null) c[e.type] += 1; });
    return c;
  }, [events]);

  const TYPE_META = {
    holiday: { label: "Holiday", dot: "bg-violet-500", chip: "bg-violet-100 text-violet-800 ring-violet-300" },
    weekend: { label: "Weekend", dot: "bg-indigo-500", chip: "bg-indigo-100 text-indigo-800 ring-indigo-300" },
    leave: { label: "Leave Taken", dot: "bg-sky-500", chip: "bg-sky-100 text-sky-800 ring-sky-300" },
    absent: { label: "Absent", dot: "bg-rose-500", chip: "bg-rose-100 text-rose-800 ring-rose-300" },
    present: { label: "Present", dot: "bg-emerald-500", chip: "bg-emerald-100 text-emerald-800 ring-emerald-300" },
    report_missing: { label: "Report Missing", dot: "bg-amber-500", chip: "bg-amber-100 text-amber-800 ring-amber-300" },
  };

  const TABS = [
    { key: "all", label: "All", count: events.length },
    { key: "holiday", label: "Holidays", count: counts.holiday },
    { key: "weekend", label: "Weekends", count: counts.weekend },
    { key: "leave", label: "Leaves", count: counts.leave },
    { key: "present", label: "Present", count: counts.present },
    { key: "absent", label: "Absent", count: counts.absent },
    { key: "report_missing", label: "Report Missing", count: counts.report_missing },
  ];

  const filteredEvents = useMemo(() => {
    const list = activeTab === "all" ? events : events.filter((e) => e.type === activeTab);
    return [...list].sort((a, b) => a.date.localeCompare(b.date));
  }, [events, activeTab]);

  const fmtLong = (iso) => {
    try {
      return new Date(iso + "T00:00:00").toLocaleDateString("en-IN", { weekday: "short", day: "2-digit", month: "short" });
    } catch { return iso; }
  };

  const shiftMonth = (delta) => {
    setViewMonth((prev) => {
      const d = new Date(prev);
      d.setDate(1);
      d.setMonth(d.getMonth() + delta);
      return d;
    });
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="dash-hol-report">
      <div className="lg:col-span-2 pk-card p-4 bg-gradient-to-br from-violet-50/30 via-white to-sky-50/30 border-slate-200" data-testid="dash-events-calendar">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600 flex items-center gap-1.5">
            <CalendarDays size={13} className="text-violet-600" /> My Events Calendar
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => shiftMonth(-1)}
              className="h-7 w-7 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 flex items-center justify-center"
              data-testid="dash-cal-prev"
              title="Previous month"
            >
              ‹
            </button>
            <div className="min-w-[140px] text-center text-[12px] font-semibold text-slate-700 mono" data-testid="dash-cal-month">
              {viewMonth.toLocaleString("en-IN", { month: "long", year: "numeric" })}
            </div>
            <button
              onClick={() => shiftMonth(1)}
              className="h-7 w-7 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 flex items-center justify-center"
              data-testid="dash-cal-next"
              title="Next month"
            >
              ›
            </button>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex items-center gap-1.5 mb-3 flex-wrap" data-testid="dash-cal-tabs">
          {TABS.map((t) => {
            const meta = TYPE_META[t.key];
            const isActive = activeTab === t.key;
            return (
              <button
                key={t.key}
                onClick={() => setActiveTab(t.key)}
                className={`h-7 px-2.5 rounded-full border text-[11px] font-semibold transition-all flex items-center gap-1.5 ${
                  isActive
                    ? "bg-[var(--pk-accent)] border-[var(--pk-accent)] text-white"
                    : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
                }`}
                data-testid={`dash-cal-tab-${t.key}`}
              >
                {meta && <span className={`inline-block h-2 w-2 rounded-full ${meta.dot}`} />}
                <span>{t.label}</span>
                <span className={`mono text-[10px] px-1 rounded ${isActive ? "bg-white/25 text-white" : "bg-slate-100 text-slate-600"}`}>
                  {t.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Event list */}
        {filteredEvents.length === 0 ? (
          <div className="text-[12px] text-slate-400 italic py-6 text-center" data-testid="dash-cal-empty">
            No {activeTab === "all" ? "events" : (TYPE_META[activeTab]?.label || activeTab).toLowerCase()} this month.
          </div>
        ) : (
          <div className="space-y-1.5" data-testid="dash-cal-list">
            {filteredEvents.map((ev, idx) => {
              const meta = TYPE_META[ev.type] || {};
              return (
                <div
                  key={`${ev.date}-${ev.type}-${idx}`}
                  className="bg-white border border-slate-200 rounded-md px-3 py-2 text-[12px] flex items-center gap-2.5"
                  data-testid={`dash-ev-${ev.type}-${ev.date}`}
                >
                  <span className={`inline-block h-2.5 w-2.5 rounded-full flex-shrink-0 ${meta.dot || "bg-slate-400"}`} />
                  <span className="mono font-semibold text-slate-700 min-w-[110px]">{fmtLong(ev.date)}</span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider ring-1 ${meta.chip || "bg-slate-100 text-slate-700 ring-slate-200"}`}>
                    {meta.label || ev.type}
                  </span>
                  <span className="text-slate-700 truncate flex-1">{ev.label}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
      {report?.is_missed ? (
        onSubmitReport ? (
          <button
            type="button"
            onClick={onSubmitReport}
            className="pk-card p-4 bg-rose-50 border-rose-200 hover:bg-rose-100 transition-all text-left w-full"
            data-testid="dash-report-missed"
          >
            <div className="flex items-start gap-2">
              <AlertCircle size={18} className="text-rose-600 mt-0.5" />
              <div>
                <div className="font-semibold text-rose-800 text-sm">Daily Report Missed</div>
                <div className="text-[11px] text-rose-700 mt-1">You haven&apos;t submitted today&apos;s report yet (past 10 PM). Click to submit now.</div>
              </div>
            </div>
          </button>
        ) : (
          <Link to="/m/hr_daily_report" className="pk-card p-4 bg-rose-50 border-rose-200 hover:bg-rose-100 transition-all" data-testid="dash-report-missed">
            <div className="flex items-start gap-2">
              <AlertCircle size={18} className="text-rose-600 mt-0.5" />
              <div>
                <div className="font-semibold text-rose-800 text-sm">Daily Report Missed</div>
                <div className="text-[11px] text-rose-700 mt-1">You haven&apos;t submitted today&apos;s report yet (past 10 PM). Click to submit now.</div>
              </div>
            </div>
          </Link>
        )
      ) : null}
    </div>
  );
}


function UpcomingInterviewsWidget() {
  const [rows, setRows] = useState([]);
  const [now, setNow] = useState(Date.now());
  const [openIE, setOpenIE] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [allInterviews, setAllInterviews] = useState([]);
  const { user } = useAuth();
  const load = async () => {
    try {
      const { data } = await api.get("/my-upcoming-interviews");
      setRows(data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    load();
    const t = setInterval(() => { setNow(Date.now()); load(); }, 60_000);
    return () => clearInterval(t);
  }, []);

  const handleOpen = async (interviewee_id) => {
    try {
      // Fetch the full interviewee + every round for this candidate (both endpoints
      // are open to any logged-in user, so an assigned interviewer can still view).
      const [ie, ix] = await Promise.all([
        api.get(`/interviewees/${interviewee_id}`),
        api.get(`/interviews?interviewee_id=${interviewee_id}`),
      ]);
      setOpenIE(ie.data);
      setAllInterviews(ix.data || []);
      // /employees is master-only — if the user is HR/Master it succeeds, otherwise we
      // silently fall back to an empty list (interviewer name is already on each round).
      try {
        const em = await api.get(`/employees`);
        setEmployees(em.data || []);
      } catch {
        setEmployees([]);
      }
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  if (!rows.length && !openIE) return null;
  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };
  return (
    <>
      {rows.length > 0 && (
        <div className="pk-depth pk-depth-violet relative overflow-hidden rounded-xl p-5 group" data-testid="dash-upcoming-interviews">
          <div className="absolute -top-10 -right-10 w-32 h-32 bg-violet-200/40 rounded-full blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="flex items-center gap-2 mb-3">
              <Users size={22} strokeWidth={2} className="text-violet-600 shrink-0 group-hover:scale-110 transition-transform" />
              <div>
                <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-violet-700">Upcoming Interviews</div>
                <div className="text-[10.5px] text-violet-600/80 font-semibold">{rows.length} scheduled</div>
              </div>
            </div>
            <ul className="space-y-2">
              {rows.slice(0, 5).map((x) => {
                const ie = x.interviewee || {};
                const t = new Date(x.scheduled_at);
                const minutesUntil = Math.round((t.getTime() - now) / 60000);
                let badgeCls = "bg-slate-100 text-slate-700";
                let badgeText = "";
                if (minutesUntil < 0) { badgeCls = "bg-rose-100 text-rose-800"; badgeText = "Overdue"; }
                else if (minutesUntil <= 5) { badgeCls = "bg-rose-200 text-rose-900 animate-pulse"; badgeText = `Starting in ${minutesUntil}m`; }
                else if (minutesUntil <= 10) { badgeCls = "bg-amber-200 text-amber-900 animate-pulse"; badgeText = `In ${minutesUntil}m`; }
                else if (minutesUntil <= 30) { badgeCls = "bg-amber-100 text-amber-800"; badgeText = `In ${minutesUntil}m`; }
                else if (minutesUntil <= 24 * 60) { badgeCls = "bg-violet-100 text-violet-700"; badgeText = `In ${Math.round(minutesUntil / 60)}h`; }
                else { badgeCls = "bg-violet-100 text-violet-700"; badgeText = `In ${Math.round(minutesUntil / 60 / 24)}d`; }
                return (
                  <li key={x.id} className="bg-white/70 backdrop-blur-sm border border-violet-100 rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap hover:border-violet-300 transition-colors" data-testid={`dash-iv-row-${x.id}`}>
                    <div className="flex-1 min-w-[200px]">
                      <div className="font-semibold text-slate-800">
                        {ie.name || "—"} <span className="text-[11px] text-slate-500">· {x.round}</span>
                      </div>
                      <div className="text-[11.5px] text-slate-500 flex flex-wrap gap-2 mt-0.5">
                        <span>📅 {fmt(x.scheduled_at)}</span>
                        {x.meeting_url ? (
                          <a href={x.meeting_url} target="_blank" rel="noreferrer" className="text-violet-700 hover:underline">Meeting Link</a>
                        ) : (
                          <span className="text-rose-700">Meeting link to be sent</span>
                        )}
                        {ie.personal_email && <span className="mono">· {ie.personal_email}</span>}
                      </div>
                    </div>
                    <span className={`text-[10.5px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ring-1 ring-current/10 ${badgeCls}`}>{badgeText}</span>
                    <button onClick={() => handleOpen(x.interviewee_id)} className="text-[11px] text-violet-700 hover:underline font-bold" data-testid={`dash-iv-open-${x.id}`}>Open →</button>
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      )}
      {openIE && (
        <InterviewDetail
          viewing={openIE}
          interviewsForIE={allInterviews}
          employees={employees}
          isAdmin={user?.role === "master_admin" || moduleCanDo(user, "hr", "edit")}
          currentUserId={user?.id}
          onClose={() => { setOpenIE(null); load(); }}
          onReload={async () => {
            try {
              const [ie, ix] = await Promise.all([
                api.get(`/interviewees/${openIE.id}`),
                api.get(`/interviews?interviewee_id=${openIE.id}`),
              ]);
              setOpenIE(ie.data);
              setAllInterviews(ix.data || []);
              load();
            } catch (e) { toast.error(formatError(e)); }
          }}
        />
      )}
    </>
  );
}

function SalaryHikeWidget({ compact = false }) {
  const [status, setStatus] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [details, setDetails] = useState(null);
  const load = async () => {
    try { const { data } = await api.get("/salary-hike/my-status"); setStatus(data); }
    catch (e) { void e; }
  };
  useEffect(() => { load(); }, []);
  // When the user expands the card, fetch the full request so we can show the
  // stage timeline + reject reason / approved message inline.
  useEffect(() => {
    if (expanded && status?.active_request_id) {
      api.get(`/salary-hike/requests/${status.active_request_id}`)
        .then(({ data }) => setDetails(data))
        .catch(() => setDetails(null));
    }
  }, [expanded, status?.active_request_id]);
  if (!status) return null;
  const locked = status.is_locked;
  const hasActive = !!status.active_request_id;

  // ---------- COMPACT (PersonalStrip section) ----------
  if (compact) {
    return (
      <>
        <div
          className="pk-depth pk-depth-fuchsia relative overflow-hidden rounded-xl p-5 group h-full"
          data-testid="strip-salary-hike"
        >
          <div className="absolute -top-8 -right-8 w-24 h-24 bg-fuchsia-200/40 rounded-full blur-2xl group-hover:bg-fuchsia-300/40 transition-colors" />
          <div className="relative">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <ChevronsUp size={22} strokeWidth={2} className="text-fuchsia-600 shrink-0 group-hover:scale-110 transition-transform" />
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-fuchsia-700">Salary Hike</div>
                  <div className="text-[10.5px] text-fuchsia-600/80 font-semibold">
                    {hasActive ? "In Progress" : locked ? "Locked" : "Available"}
                  </div>
                </div>
              </div>
              {locked && <Lock size={14} className="text-slate-400 flex-shrink-0 mt-1" />}
            </div>

            <div className="mt-3">
              {hasActive ? (
                <>
                  <div className="text-[13px] font-bold text-slate-800 leading-snug capitalize">
                    {(status.active_stage || "").replace(/_/g, " ")}
                  </div>
                  <div className="text-[10.5px] text-slate-500 mt-0.5">
                    Active request in review
                  </div>
                </>
              ) : locked ? (
                <>
                  <div className="text-[13px] font-bold text-slate-800 leading-snug">
                    Locked till <span className="mono">{status.locked_until}</span>
                  </div>
                  <div className="text-[10.5px] text-slate-500 mt-0.5">
                    Try again after that date
                  </div>
                </>
              ) : (
                <>
                  <div className="text-[13px] font-bold text-slate-800 leading-snug">
                    Want a raise?
                  </div>
                  <div className="text-[10.5px] text-slate-500 mt-0.5">
                    Routed via Manager → HR → Master
                  </div>
                </>
              )}
            </div>

            <div className="mt-3">
              {hasActive ? (
                <button
                  onClick={() => setExpanded((v) => !v)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white border border-violet-200 text-[10.5px] font-bold text-violet-700 hover:bg-violet-50 hover:border-violet-300 transition-all"
                  data-testid="strip-sh-track"
                >
                  {expanded ? "Hide" : "Track request"} <ArrowUpRight size={11} />
                </button>
              ) : !locked ? (
                <button
                  onClick={() => setShowDialog(true)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 text-[10.5px] font-bold text-white shadow-sm hover:shadow-md hover:-translate-y-px transition-all"
                  data-testid="strip-sh-submit"
                >
                  Submit Request <ArrowUpRight size={11} />
                </button>
              ) : null}
            </div>

            {hasActive && expanded && (
              <div className="mt-3 -mx-1">
                <SalaryHikeTracker details={details} status={status} />
              </div>
            )}
          </div>
        </div>
        {showDialog && (
          <SalaryHikeSubmitDialog
            open={showDialog}
            onClose={() => setShowDialog(false)}
            onSubmitted={() => { setShowDialog(false); load(); }}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div className="pk-card p-4" data-testid="dashboard-salary-hike">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2.5">
            <div className="w-9 h-9 rounded-md bg-violet-100 flex items-center justify-center">
              <TrendingUp size={18} className="text-violet-700" />
            </div>
            <div>
              <div className="text-[13px] font-bold text-slate-800">Salary Hike</div>
              <div className="text-[11px] text-slate-500">
                {hasActive
                  ? <>You have an active request <b>{(status.active_stage || "").replace(/_/g, " ")}</b>.</>
                  : locked
                  ? <>Locked until <b>{status.locked_until}</b> &mdash; try again after that date.</>
                  : "Want a raise? Submit your justification \u2014 it routes through your manager \u2192 HR \u2192 Master Admin."}
              </div>
            </div>
          </div>
          {hasActive ? (
            <Button variant="outline" onClick={() => setExpanded((v) => !v)} className="h-9 text-[12px]" data-testid="dashboard-sh-track">
              {expanded ? "Hide" : "Track your request"}
            </Button>
          ) : locked ? (
            <Button disabled className="bg-slate-300 text-slate-600 cursor-not-allowed" data-testid="dashboard-sh-locked">
              <Lock size={13} className="mr-1.5" /> Locked
            </Button>
          ) : (
            <Button onClick={() => setShowDialog(true)} className="bg-violet-600 hover:bg-violet-700" data-testid="dashboard-sh-submit">
              <TrendingUp size={13} className="mr-1.5" /> Submit Salary Hike Request
            </Button>
          )}
        </div>

        {hasActive && expanded && (
          <SalaryHikeTracker details={details} status={status} />
        )}
      </div>
      {showDialog && (
        <SalaryHikeSubmitDialog
          open={showDialog}
          onClose={() => setShowDialog(false)}
          onSubmitted={() => { setShowDialog(false); load(); }}
        />
      )}
    </>
  );
}

function SalaryHikeTracker({ details, status }) {
  if (!details) return <div className="mt-3 text-[11.5px] text-slate-500">Loading details&hellip;</div>;
  const stage = details.stage;
  const steps = [
    { key: "employee_submitted", label: "Submitted" },
    { key: "manager_assigned", label: "Manager Review" },
    { key: "hr_assigned", label: "HR Review" },
    { key: "approved", label: "Final Approval" },
  ];
  const order = { employee_submitted: 0, manager_assigned: 1, manager_reviewed: 1, hr_assigned: 2, hr_reviewed: 2, approved: 3, rejected: -1 };
  const idx = order[stage] ?? 0;
  return (
    <div className="mt-4 border-t border-slate-100 pt-3 space-y-3" data-testid="dashboard-sh-tracker">
      {/* Stage timeline */}
      <div className="flex items-center gap-1.5 text-[10.5px] font-semibold flex-wrap">
        {steps.map((s, i) => {
          const done = stage === "approved" || idx > i;
          const active = idx === i && stage !== "approved" && stage !== "rejected";
          return (
            <div key={s.key} className="flex items-center gap-1.5">
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[9px] ${
                done ? "bg-emerald-500 text-white" :
                active ? "bg-violet-600 text-white" :
                "bg-slate-200 text-slate-500"
              }`}>{done ? "\u2713" : i + 1}</span>
              <span className={done || active ? "text-slate-800" : "text-slate-400"}>{s.label}</span>
              {i < steps.length - 1 && <span className="w-3 h-px bg-slate-300 mx-0.5" />}
            </div>
          );
        })}
      </div>

      {/* In-process / approved / rejected message */}
      {stage === "approved" && (
        <div className="rounded-md border-2 border-emerald-300 bg-emerald-50 p-3 text-[12.5px] text-emerald-900" data-testid="dashboard-sh-approved-msg">
          <b>\u2713 Approved.</b> For further details, please check your <b>official email</b>.
          {details.final_form?.approved_salary && (
            <div className="mt-1 text-[12px]">
              New monthly salary: <b className="mono">&#8377; {Number(details.final_form.approved_salary).toLocaleString("en-IN")}</b>
              {details.final_form.effective_date && <> &middot; effective <b>{details.final_form.effective_date}</b></>}
            </div>
          )}
        </div>
      )}
      {stage === "rejected" && (
        <div className="rounded-md border-2 border-rose-300 bg-rose-50 p-3 text-[12.5px] text-rose-900" data-testid="dashboard-sh-rejected-msg">
          <b>Request Rejected.</b>
          {details.rejected_reason && (
            <div className="mt-1 italic">&ldquo;{details.rejected_reason}&rdquo;</div>
          )}
          {details.rejected_by && (
            <div className="mt-1 text-[10.5px] text-rose-700">By {details.rejected_by} &middot; {(details.rejected_at || "").slice(0, 10)}</div>
          )}
        </div>
      )}
      {stage !== "approved" && stage !== "rejected" && (
        <div className="rounded-md border border-violet-200 bg-violet-50/60 p-3 text-[12.5px] text-violet-900" data-testid="dashboard-sh-inprocess-msg">
          <b>In Process</b> &middot; currently {stage.replace(/_/g, " ")}.
          <div className="text-[11px] text-slate-600 mt-0.5">
            You&rsquo;ll be notified by email when there&rsquo;s an update. The button on this dashboard will show the latest status.
          </div>
        </div>
      )}

      {status.locked_until && (
        <div className="text-[10.5px] text-slate-500">
          Button is locked till <b>{status.locked_until}</b>.
        </div>
      )}
    </div>
  );
}


function PendingDateConfirmationsBanner() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);

  useEffect(() => {
    if (!user) return;
    if (!["trainer", "master_admin", "sales_admin"].includes(user.role)) return;
    api.get("/trainer-assignments/pending-confirmations")
      .then(({ data }) => setItems(data?.items || []))
      .catch(() => {});
  }, [user?.id, user?.role]);

  if (items.length === 0) return null;
  const isTrainer = user?.role === "trainer";

  return (
    <div className="pk-card p-4 border-amber-300 bg-amber-50 dark:bg-amber-900/15 dark:border-amber-700" data-testid="dash-pending-confirm-banner">
      <div className="flex items-center gap-2 mb-2">
        <Phone size={16} className="text-amber-700 dark:text-amber-400" />
        <h3 className="heading text-base font-bold text-amber-900 dark:text-amber-200">
          {isTrainer ? "Action needed: Call the school to confirm training dates" : "Trainers with pending school-confirmation calls"}
        </h3>
        <span className="ml-auto text-[11px] mono bg-amber-200/70 dark:bg-amber-900/40 text-amber-900 dark:text-amber-200 px-2 py-0.5 rounded-full">{items.length}</span>
      </div>
      <div className="space-y-1.5">
        {items.slice(0, 5).map((a) => (
          <Link key={a.id} to="/m/trainers" className="flex items-center justify-between gap-3 bg-white dark:bg-slate-900 border border-amber-200 dark:border-amber-700 rounded-md px-3 py-2 hover:bg-amber-100/50 dark:hover:bg-amber-900/20 transition-colors" data-testid={`dash-pending-confirm-${a.id}`}>
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-slate-900 dark:text-slate-100 truncate">{a.client_name}</div>
              <div className="text-[11px] text-slate-600 dark:text-slate-300 truncate">
                {a.training_type === "Other" ? (a.type_other_name || "Other") : a.training_type} · {a.training_from} → {a.training_to}
                {!isTrainer && a.trainer_name && <span className="text-slate-500"> · Trainer: {a.trainer_name}</span>}
              </div>
            </div>
            <span className="text-[10px] mono text-amber-700 dark:text-amber-300 shrink-0">Confirm dates →</span>
          </Link>
        ))}
        {items.length > 5 && (
          <Link to="/m/trainers" className="block text-[11px] text-amber-800 dark:text-amber-300 underline mt-1">
            + {items.length - 5} more
          </Link>
        )}
      </div>
    </div>
  );
}


function TrainerAssignmentsWidget() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.role !== "trainer") return;
    setLoading(true);
    api.get("/trainer-assignments")
      .then(({ data }) => setList(data || []))
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, [user?.id, user?.role]);

  if (user?.role !== "trainer") return null;

  const uid = user?.id;
  const assigned = list.filter((a) => a.trainer_id === uid && (a.status === "Assigned" || a.status === "In Progress"));
  const upcoming = list.filter((a) => a.status === "Upcoming");
  // "Review requests" = anything I'm waiting on: pending date-change requests + pending assignment requests
  const pendingDateReqs = list.filter((a) =>
    a.trainer_id === uid && (a.date_change_requests || []).some((r) => r.status === "Pending")
  );
  const myAssignmentRequests = upcoming.filter((a) =>
    (a.pending_requests || []).some((r) => r.trainer_id === uid)
  );
  const reviewQueue = [...pendingDateReqs, ...myAssignmentRequests];

  return (
    <div className="pk-card p-4" data-testid="trainer-dash-widget">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div>
          <h3 className="heading text-base font-bold text-slate-900 dark:text-slate-100">My Trainings</h3>
          <p className="text-[11px] text-slate-500">Assigned · Upcoming pool · Requests under review</p>
        </div>
        <Link to="/m/trainers" className="text-[11.5px] text-[var(--pk-accent)] hover:underline" data-testid="trainer-dash-open-all">
          Open Trainings module →
        </Link>
      </div>

      <Tabs defaultValue="assigned">
        <TabsList data-testid="trainer-dash-tabs">
          <TabsTrigger value="assigned" data-testid="trainer-dash-tab-assigned">
            Assigned <CountPill n={assigned.length} tone="emerald" />
          </TabsTrigger>
          <TabsTrigger value="upcoming" data-testid="trainer-dash-tab-upcoming">
            Upcoming <CountPill n={upcoming.length} tone="sky" />
          </TabsTrigger>
          <TabsTrigger value="review" data-testid="trainer-dash-tab-review">
            Review Requests <CountPill n={reviewQueue.length} tone="amber" />
          </TabsTrigger>
        </TabsList>

        <TabsContent value="assigned" className="mt-3">
          {loading && <div className="text-center py-6 text-slate-400 text-sm">Loading…</div>}
          {!loading && assigned.length === 0 && (
            <div className="text-center py-6 text-slate-400 text-sm" data-testid="trainer-dash-assigned-empty">
              You don&apos;t have any trainings assigned right now.
            </div>
          )}
          <div className="space-y-1.5">
            {assigned.map((a) => <TrainingRow key={a.id} a={a} kind="assigned" />)}
          </div>
        </TabsContent>

        <TabsContent value="upcoming" className="mt-3">
          {loading && <div className="text-center py-6 text-slate-400 text-sm">Loading…</div>}
          {!loading && upcoming.length === 0 && (
            <div className="text-center py-6 text-slate-400 text-sm" data-testid="trainer-dash-upcoming-empty">
              No upcoming trainings open for trainer requests right now.
            </div>
          )}
          <div className="space-y-1.5">
            {upcoming.map((a) => {
              const already = (a.pending_requests || []).some((r) => r.trainer_id === uid);
              return <TrainingRow key={a.id} a={a} kind="upcoming" highlight={already} />;
            })}
          </div>
        </TabsContent>

        <TabsContent value="review" className="mt-3">
          {loading && <div className="text-center py-6 text-slate-400 text-sm">Loading…</div>}
          {!loading && reviewQueue.length === 0 && (
            <div className="text-center py-6 text-slate-400 text-sm" data-testid="trainer-dash-review-empty">
              No requests waiting on Master Admin review.
            </div>
          )}
          <div className="space-y-1.5">
            {pendingDateReqs.map((a) => {
              const req = (a.date_change_requests || []).find((r) => r.status === "Pending");
              return (
                <TrainingRow key={`d-${a.id}`} a={a} kind="date-request" subtitle={
                  req ? `Date change ${req.requested_from} → ${req.requested_to} · ${req.reason}` : ""
                } />
              );
            })}
            {myAssignmentRequests.map((a) => (
              <TrainingRow key={`r-${a.id}`} a={a} kind="assign-request" subtitle="Awaiting admin approval for your request" />
            ))}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function CountPill({ n, tone = "slate" }) {
  const map = {
    emerald: "bg-emerald-100 text-emerald-800",
    sky: "bg-sky-100 text-sky-800",
    amber: "bg-amber-100 text-amber-800",
    slate: "bg-slate-100 text-slate-700",
  };
  if (!n) return null;
  return <span className={`ml-1.5 text-[10px] font-bold rounded-full px-1.5 py-0.5 ${map[tone]}`}>{n}</span>;
}

function TrainingRow({ a, kind, subtitle, highlight }) {
  const typeLabel = a.training_type === "Other" ? (a.type_other_name || "Other") : a.training_type;
  const confirmed = a.date_confirmation_status === "confirmed";
  return (
    <Link
      to="/m/trainers"
      className={`flex items-center justify-between gap-3 rounded-md px-3 py-2 border transition-colors ${
        highlight
          ? "border-amber-200 bg-amber-50/60 hover:bg-amber-100/60 dark:bg-amber-900/15 dark:border-amber-700"
          : "border-slate-200 bg-white hover:bg-slate-50 dark:bg-slate-900 dark:border-slate-700 dark:hover:bg-slate-800"
      }`}
      data-testid={`trainer-dash-row-${kind}-${a.id}`}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[13px] font-semibold text-slate-900 dark:text-slate-100 truncate">{a.client_name}</span>
          <span className="text-[10px] mono text-slate-500">· {typeLabel}</span>
          {kind === "assigned" && !confirmed && (
            <span className="text-[9.5px] font-bold bg-amber-200/70 text-amber-900 dark:bg-amber-900/40 dark:text-amber-200 rounded-full px-1.5 py-0.5">Confirm dates</span>
          )}
          {kind === "assigned" && confirmed && (
            <span className="text-[9.5px] font-bold bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300 rounded-full px-1.5 py-0.5">✓ Confirmed</span>
          )}
          {kind === "date-request" && (
            <span className="text-[9.5px] font-bold bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300 rounded-full px-1.5 py-0.5">Date change pending</span>
          )}
          {kind === "assign-request" && (
            <span className="text-[9.5px] font-bold bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300 rounded-full px-1.5 py-0.5">You requested</span>
          )}
        </div>
        <div className="text-[11px] text-slate-600 dark:text-slate-300 truncate mt-0.5">
          <span className="mono">{a.training_from} → {a.training_to}</span>
          {a.client_address && <span className="text-slate-400"> · {a.client_address}</span>}
        </div>
        {subtitle && (
          <div className="text-[10.5px] text-slate-500 dark:text-slate-400 truncate mt-0.5">{subtitle}</div>
        )}
      </div>
      <span className="text-[10px] mono text-slate-400 shrink-0">Open →</span>
    </Link>
  );
}


function TesterReviewsWidget() {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user?.role !== "trainer") return;
    setLoading(true);
    api.get("/trainer-projects")
      .then(({ data }) => setProjects(data || []))
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  }, [user?.id, user?.role]);

  if (user?.role !== "trainer") return null;

  const uid = user?.id;
  // Only surface projects assigned to me for testing, excluding ones I created
  // (a creator can't be their own tester anyway). Hide Completed — the trainer
  // can still find them in the Projects tab if needed.
  const myReviews = projects.filter(
    (p) => p.tester_id === uid && p.status !== "Completed"
  );

  if (loading) {
    return (
      <div className="pk-card p-4" data-testid="tester-reviews-widget">
        <div className="text-[13px] text-slate-400">Loading review queue…</div>
      </div>
    );
  }
  if (myReviews.length === 0) return null;

  const STATUS_TONE = {
    "Submitted": { chip: "bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300", label: "Awaiting your test" },
    "Under Test": { chip: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300", label: "In progress" },
    "Needs Fix": { chip: "bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300", label: "Sent back to creator" },
  };

  // Sort: Submitted first (action needed), then Under Test, then Needs Fix
  const order = { "Submitted": 0, "Under Test": 1, "Needs Fix": 2 };
  const sorted = [...myReviews].sort((a, b) => (order[a.status] ?? 9) - (order[b.status] ?? 9));

  return (
    <div className="pk-card p-4" data-testid="tester-reviews-widget">
      <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
        <div>
          <h3 className="heading text-base font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Eye size={15} className="text-violet-600 dark:text-violet-400" />
            Projects to Review
            <span className="text-[10px] font-bold bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200 rounded-full px-1.5 py-0.5">{myReviews.length}</span>
          </h3>
          <p className="text-[11px] text-slate-500">You&apos;ve been assigned as the Tester. Review and either approve or send back with feedback.</p>
        </div>
        <Link to="/m/trainers" className="text-[11.5px] text-[var(--pk-accent)] hover:underline" data-testid="tester-reviews-open">
          Open Trainer Projects →
        </Link>
      </div>
      <div className="space-y-1.5">
        {sorted.map((p) => {
          const tone = STATUS_TONE[p.status] || { chip: "bg-slate-100 text-slate-700", label: p.status };
          return (
            <Link
              key={p.id}
              to="/m/trainers"
              className="flex items-center justify-between gap-3 rounded-md px-3 py-2 border border-slate-200 bg-white hover:bg-slate-50 dark:bg-slate-900 dark:border-slate-700 dark:hover:bg-slate-800 transition-colors"
              data-testid={`tester-review-row-${p.id}`}
            >
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[13px] font-semibold text-slate-900 dark:text-slate-100 truncate">{p.project_name}</span>
                  <span className={`text-[9.5px] font-bold rounded-full px-1.5 py-0.5 ${tone.chip}`}>{tone.label}</span>
                  {p.resubmitted_at && (
                    <span className="text-[9.5px] font-bold bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300 rounded-full px-1.5 py-0.5">Resubmitted</span>
                  )}
                </div>
                <div className="text-[11px] text-slate-600 dark:text-slate-300 truncate mt-0.5">
                  By <b>{p.created_by_name}</b>
                  {p.project_objective && <span className="text-slate-500"> · {p.project_objective}</span>}
                </div>
                {p.tester_review && p.status === "Needs Fix" && (
                  <div className="text-[10.5px] italic text-slate-500 dark:text-slate-400 truncate mt-0.5">
                    Your feedback: &ldquo;{p.tester_review}&rdquo;
                  </div>
                )}
              </div>
              <span className="text-[10px] mono text-slate-400 shrink-0">Review →</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
}

